import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Severity } from '@gripp/shared-logic';
import { ReactNode } from 'react';
import {
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle
} from 'react-native';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { SeverityIcons } from '../../issue/severityIcons';

type RoutineButtonProps = {
  onClick: () => void;
  text: string;
  buttonStyle?: StyleProp<ViewStyle>;
  buttonIcon?: Severity;
  buttonIconStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<ViewStyle>;
  buttonLabelStyle?: StyleProp<TextStyle>;
  leftButtonIcon?: ReactNode;
  children?: ReactNode;
  underlineInfoText?: string;
  secondaryButtonIcon?: ReactNode;
};

export const RoutineButton = (props: RoutineButtonProps) => {
  return (
    <TouchableOpacity
      onPress={props.onClick}
      style={[styles.button, props.buttonStyle]}
      activeOpacity={0.7}
    >
      <View style={styles.leftIconContainer}>
        {/* Status/severity icon */}
        {props.buttonIcon && (
          <MaterialCommunityIcons
            name={SeverityIcons[props.buttonIcon]}
            size={16}
            style={[styles.icon, { marginRight: 0, marginLeft: 4 }]}
          />
        )}
        
        {props.secondaryButtonIcon}
        
        {/* Left button icon (draft indicator, etc.) */}
        {props.leftButtonIcon && (
          <View style={{ marginLeft: 4 }}>{props.leftButtonIcon}</View>
        )}
      </View>

      <Text
        style={[styles.buttonLabel, props.buttonLabelStyle, props.labelStyle]}
        numberOfLines={1}
      >
        {props.text}
      </Text>
      
      {props.underlineInfoText && (
        <Text style={styles.infoText}>{props.underlineInfoText}</Text>
      )}
      {props.children}
      
      <View style={styles.rightIconContainer}>
        <Icon name="Chevron Right" size={20} color={ON_INFO_FILL} />
      </View>
    </TouchableOpacity>
  );
};

// The button fill is a semantic light blue, which is a constant: semantic
// hues do not flip with the theme. So everything sitting ON it has to be a
// constant too. `Colors.role.textStrong` was used here, and it flips to white
// in dark mode - which is how this ended up as white-on-pale-blue. #020202 is
// exactly what textStrong resolved to in light, so light mode is unchanged.
const ON_INFO_FILL = tokens.colors.neutral[800];

const styles = StyleSheet.create({
  button: {
    backgroundColor: tokens.colors.semantic.infoLight,
    borderWidth: 0,
    minHeight: 48,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginVertical: 1,
  },
  infoText: {
    color: ON_INFO_FILL,
  },
  buttonLabel: {
    ...tokens.typography.body.mediumEmphasised,
    color: ON_INFO_FILL,
    flex: 1,
    marginLeft: tokens.spacing.sm,
  },
  icon: {
    color: ON_INFO_FILL,
    marginRight: tokens.spacing.sm,
  },
  rightIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: tokens.spacing.sm,
    marginLeft: 'auto',
  },
  leftIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
